import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingBag,
  ChevronRight,
  DollarSign,
  Users,
  LineChart,
} from "lucide-react";
import { useAdminAuth } from "@/context/Admin";
import { Link } from "react-router-dom";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";

export default function Overview() {
  const [greeting, setGreeting] = useState("");
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const { admin, getMonthlyStats, getRecentOrders } = useAdminAuth();

  useEffect(() => {
    const hour = new Date().getHours();
    setGreeting(
      hour < 12
        ? "Good morning"
        : hour < 18
          ? "Good afternoon"
          : "Good evening",
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const stats = await getMonthlyStats();
        setMonthlyStats(stats);

        const orders = await getRecentOrders();
        setRecentOrders(orders);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      }
    };
    fetchData();
  }, [getMonthlyStats, getRecentOrders]);

  // Currency formatter for NGN
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
    }).format(amount || 0);

  // Format order date
  const formatOrderDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
        if (diffHours === 0) {
          const diffMinutes = Math.floor(diffTime / (1000 * 60));
          return diffMinutes <= 1 ? "Just now" : `${diffMinutes} minutes ago`;
        }
        return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
      } else if (diffDays === 1) {
        return "Yesterday";
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year:
            date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
        });
      }
    } catch (error) {
      return dateString;
    }
  };

  // Calculate stats and percentages
  const currentStats =
    monthlyStats.length > 0
      ? monthlyStats[monthlyStats.length - 1]
      : {
          total_sales: 0,
          new_customers: 0,
          products_sold: 0,
        };

  const previousStats =
    monthlyStats.length > 1
      ? monthlyStats[monthlyStats.length - 2]
      : {
          total_sales: 0,
          new_customers: 0,
          products_sold: 0,
        };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const salesChange = calculateChange(
    currentStats.total_sales,
    previousStats.total_sales,
  );
  const customersChange = calculateChange(
    currentStats.new_customers,
    previousStats.new_customers,
  );
  const productsChange = calculateChange(
    currentStats.products_sold,
    previousStats.products_sold,
  );

  const formatPercentage = (val) => {
    const prefix = val >= 0 ? "+" : "";
    return `${prefix}${val.toFixed(1)}%`;
  };

  const getChangeColor = (val) => {
    if (val > 0) return "text-green-600";
    if (val < 0) return "text-red-600";
    return "text-gray-500";
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Key Metrics Cards */}
      <h1 className="text-2xl font-bold mb-4">
        {greeting}, {admin?.name}
      </h1>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Sales</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(currentStats.total_sales)}
            </div>
            <p className={`text-xs ${getChangeColor(salesChange)}`}>
              {formatPercentage(salesChange)} from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">New Customers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              +{currentStats.new_customers}
            </div>
            <p className={`text-xs ${getChangeColor(customersChange)}`}>
              {formatPercentage(customersChange)} from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Products Sold</CardTitle>
            <ShoppingBag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {currentStats.products_sold?.toLocaleString()}
            </div>
            <p className={`text-xs ${getChangeColor(productsChange)}`}>
              {formatPercentage(productsChange)} from last month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Website Traffic
            </CardTitle>
            <LineChart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">-</div>
            <p className="text-xs text-muted-foreground">Not yet integrated</p>
          </CardContent>
        </Card>
      </div>

      {/* Sales Overview with Chart */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card className="col-span-4">
          <CardHeader>
            <CardTitle>Sales Overview</CardTitle>
            <CardDescription>
              Monthly sales, new customers, and products sold.
            </CardDescription>
          </CardHeader>
          <CardContent className="pl-2">
            <ResponsiveContainer width="100%" height={300}>
              <ReLineChart
                data={monthlyStats}
                margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip
                  formatter={(value, name) =>
                    name === "total_sales"
                      ? [formatCurrency(value), "Total Sales"]
                      : [value, name.replace("_", " ")]
                  }
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total_sales"
                  stroke="#22c55e"
                  name="Total Sales"
                />
                <Line
                  type="monotone"
                  dataKey="new_customers"
                  stroke="#3b82f6"
                  name="New Customers"
                />
                <Line
                  type="monotone"
                  dataKey="products_sold"
                  stroke="#f59e0b"
                  name="Products Sold"
                />
              </ReLineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Placeholder for recent activity */}
        <Card className="col-span-3">
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>
              Latest actions and events in the dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-500 text-center py-4">
              Recent activity integration coming soon.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-primary">Recent Orders</CardTitle>
            <CardDescription>Your latest customer orders.</CardDescription>
          </div>
          <Link to="/admin/orders/list">
            <Button
              variant="ghost"
              className="text-secondary hover:text-secondary/80"
            >
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent className="space-y-4">
          {recentOrders.length > 0 ? (
            recentOrders.map((order, index) => (
              <Link
                key={order.order_id}
                to={`/admin/orders/${order.order_id}`}
                className="block hover:bg-gray-50 rounded-lg transition-colors"
              >
                <div className="flex items-center justify-between py-3 px-2">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    {/* Product Images */}
                    <div className="flex -space-x-2 flex-shrink-0">
                      {order.product_images &&
                      order.product_images.length > 0 ? (
                        order.product_images
                          .slice(0, 3)
                          .map((imageUrl, imgIndex) => (
                            <Avatar
                              key={imgIndex}
                              className="w-10 h-10 border-2 border-white ring-2 ring-gray-100"
                            >
                              <AvatarImage
                                src={imageUrl}
                                alt={`Product ${imgIndex + 1}`}
                                className="object-cover"
                              />
                              <AvatarFallback className="bg-gray-200 text-gray-500 text-xs">
                                {imgIndex + 1}
                              </AvatarFallback>
                            </Avatar>
                          ))
                      ) : (
                        <Avatar className="w-10 h-10 border-2 border-white ring-2 ring-gray-100">
                          <AvatarFallback className="bg-gray-200 text-gray-500">
                            <ShoppingBag className="w-4 h-4" />
                          </AvatarFallback>
                        </Avatar>
                      )}
                      {order.product_images &&
                        order.product_images.length > 3 && (
                          <Avatar className="w-10 h-10 border-2 border-white ring-2 ring-gray-100">
                            <AvatarFallback className="bg-gray-100 text-gray-600 text-xs font-medium">
                              +{order.product_images.length - 3}
                            </AvatarFallback>
                          </Avatar>
                        )}
                    </div>

                    {/* Order Info */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-primary truncate">
                        Order #{order.order_id}
                      </p>
                      <p className="text-sm text-gray-600 truncate">
                        {formatOrderDate(order.order_date)} •{" "}
                        {order.number_of_items}{" "}
                        {order.number_of_items === 1 ? "item" : "items"}
                        {order.total > 0 && ` • ${formatCurrency(order.total)}`}
                      </p>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                    <Badge
                      className={`${order.status === "Delivered" ? "bg-green-100 text-green-700" : order.status === "Shipped" ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}
                    >
                      {order.status}
                    </Badge>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
                {index < recentOrders.length - 1 && <Separator />}
              </Link>
            ))
          ) : (
            <p className="text-gray-500 text-center py-4">
              No recent orders found.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
